import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// Tester-only rating of a stored assistant answer (feature AI_ANSWER_REVIEW). The server keeps the review on the answer row.
// Each rating has its own reason codes, the same sets btax-api accepts.
export const REVIEW_REASONS = {
  up: [
    { code: "ACCURATE", label: "תשובה מדויקת" },
    { code: "CLEAR", label: "ברור ופשוט" },
    { code: "SAVED_TIME", label: "חסך לי זמן" },
    { code: "HELPED_CALC", label: "עזר לי בחישוב" },
    { code: "OTHER", label: "אחר" }
  ],
  down: [
    { code: "WRONG_INFO", label: "מידע שגוי" },
    { code: "MISUNDERSTOOD", label: "ענה על שאלה אחרת" },
    { code: "UNCLEAR", label: "התשובה לא מובנת" },
    { code: "OTHER", label: "אחר" }
  ]
};
const SHEET_TEXT = {
  up: { title: "מה עבד טוב?", placeholder: "רוצה להוסיף משהו במילים שלך?" },
  down: { title: "מה היה חסר?", placeholder: "מה הייתה התשובה שציפית לקבל?" }
};
export const MAX_REVIEW_COMMENT_CHARACTERS = 400;
// Matches the sheet slide-down in AiAssistantPanel.css; the closed sheet leaves the DOM after it.
export const REVIEW_SHEET_EXIT_MS = 340;

const BURST_MS = 600;
const BURST_SPARKS = [0, 51, 102, 153, 204, 255, 306];

const THUMB_UP_PATH = "M7 11v8a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-7a1 1 0 0 1 1 -1h3a4 4 0 0 0 4 -4v-1a2 2 0 0 1 4 0v5h3a2 2 0 0 1 2 2l-1 5a2 3 0 0 1 -2 2h-7a3 3 0 0 1 -3 -3";
const THUMB_DOWN_PATH = "M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2 -2l-1 -5a2 3 0 0 0 -2 -2h-7a3 3 0 0 0 -3 3";

function ThumbIcon({ path }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d={path} /></svg>;
}

// Native disabled controls can lose focus before an effect runs. Remember the control before disabling,
// park on its stable container, and restore only while focus still belongs to that fallback.
function useReviewFocus(saving, coolingDown, leaving) {
  const containerRef = useRef(null);
  const focusedControlRef = useRef(null);
  const restoreControlRef = useRef(null);
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || leaving) return;
    const focused = document.activeElement;
    const restore = restoreControlRef.current;
    if (restore) {
      if (!container.contains(restore) || (focused !== container && focused !== document.body)) restoreControlRef.current = null;
      else if (!restore.disabled) {
        restoreControlRef.current = null;
        restore.focus({ preventScroll: true });
        return;
      }
    }
    const control = focusedControlRef.current;
    if (control?.disabled && container.contains(control) && (focused === control || focused === document.body)) {
      restoreControlRef.current = control;
      container.focus({ preventScroll: true });
    }
  }, [saving, coolingDown, leaving]);
  return { ref: containerRef, tabIndex: -1, onFocusCapture: (event) => {
    if (event.target !== event.currentTarget) focusedControlRef.current = event.target;
  }, onBlurCapture: (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    if (event.target.disabled && (!event.relatedTarget || event.relatedTarget === document.body)) return;
    focusedControlRef.current = null;
    restoreControlRef.current = null;
  } };
}

// Either rating offers "רוצה לפרט?"; the link is red after a thumbs-down, where details help most.
// A new rating pops its thumb, and a thumbs-up also throws a short burst of sparks.
export function AnswerReviewButtons({ turnId, review, saving, failed, disabled, latest, arriving, onRate, onEditComment }) {
  const focusProps = useReviewFocus(saving, disabled);
  const rating = review?.rating || null;
  const hasDetails = Boolean(rating && (review.comment || review.reasons?.length));
  const [pop, setPop] = useState({ rating: null, key: 0 });
  const [burst, setBurst] = useState(0);
  const burstTimeoutRef = useRef(null);
  useEffect(() => () => window.clearTimeout(burstTimeoutRef.current), []);

  const rate = (next) => {
    if (saving || disabled) return;
    if (rating !== next) {
      setPop((current) => ({ rating: next, key: current.key + 1 }));
      if (next === "up") {
        setBurst((current) => current + 1);
        window.clearTimeout(burstTimeoutRef.current);
        burstTimeoutRef.current = window.setTimeout(() => setBurst(0), BURST_MS);
      }
    }
    onRate(next);
  };
  const thumb = (value, label, path) => <button type="button" className={`ai-assistant-review-thumb ai-assistant-review-thumb-${value}${pop.rating === value ? " ai-assistant-review-pop" : ""}`} aria-label={label} aria-pressed={rating === value} disabled={disabled} aria-disabled={saving || disabled || undefined} onClick={() => rate(value)}>
    <ThumbIcon key={`icon-${pop.rating === value ? pop.key : 0}`} path={path} />
    {value === "up" && burst ? <span key={`burst-${burst}`} className="ai-assistant-review-burst" aria-hidden="true">{BURST_SPARKS.map((angle) => <i key={angle} style={{ "--spark-angle": `${angle}deg` }} />)}</span> : null}
  </button>;

  return <div {...focusProps} className={`ai-assistant-review${rating === "down" ? " ai-assistant-review-is-down" : ""}${latest ? " ai-assistant-review-latest" : ""}${arriving ? " ai-assistant-review-after-reply" : ""}`} role="group" aria-label="דירוג התשובה">
    {thumb("up", "תשובה טובה", THUMB_UP_PATH)}
    {thumb("down", "תשובה לא טובה", THUMB_DOWN_PATH)}
    {rating ? <span key={`${rating}-${hasDetails}`} className="ai-assistant-review-note">{hasDetails
      ? <><span className="ai-assistant-review-done">קיבלנו את הפירוט, תודה</span><button type="button" className="ai-assistant-review-more" data-review-turn={turnId} aria-label="עריכת הפירוט" disabled={saving || disabled} onClick={onEditComment}>עריכה</button></>
      : <><span>תודה!</span><button type="button" className="ai-assistant-review-more" data-review-turn={turnId} disabled={saving || disabled} onClick={onEditComment}>רוצה לפרט?</button></>}</span> : null}
    {failed ? <span className="ai-assistant-review-failed" role="alert">הדירוג לא נשמר. נסו שוב.</span> : null}
  </div>;
}

// A bottom sheet inside the panel only. It opens from the "רוצה לפרט?" link, never by itself; while leaving it is hidden from assistive technology.
export function AnswerReviewDialog({ review, leaving, saving, failed, coolingDown, onSave, onCancel }) {
  const focusProps = useReviewFocus(saving, coolingDown, leaving);
  const [rating] = useState(() => review?.rating === "up" ? "up" : "down");
  const [reasons, setReasons] = useState(review?.reasons || []);
  const [comment, setComment] = useState(review?.comment || "");
  const [hadDetails] = useState(() => Boolean(review?.comment || review?.reasons?.length));
  const firstReasonRef = useRef(null);
  useEffect(() => { firstReasonRef.current?.focus({ preventScroll: true }); }, []);
  const toggleReason = (code) => setReasons((current) => current.includes(code) ? current.filter((item) => item !== code) : [...current, code]);
  const canSend = reasons.length > 0 || Boolean(comment.trim()) || hadDetails;
  const save = (event) => {
    event.preventDefault();
    if (!canSend || leaving || saving || coolingDown) return;
    onSave({ rating, reasons: REVIEW_REASONS[rating].map((reason) => reason.code).filter((code) => reasons.includes(code)), comment: comment.trim() || null });
  };
  return <div className={`ai-assistant-review-layer${leaving ? " ai-assistant-review-layer-leaving" : ""}`} aria-hidden={leaving || undefined}>
    <div className="ai-assistant-review-scrim" onMouseDown={leaving ? undefined : onCancel} />
    <form {...focusProps} className={`ai-assistant-review-sheet ai-assistant-review-sheet-${rating}`} role="dialog" aria-modal="true" aria-labelledby="ai-assistant-review-title" aria-describedby="ai-assistant-review-subtitle" onSubmit={save}>
      <div className="ai-assistant-review-grab" aria-hidden="true" />
      <div className="ai-assistant-review-sheet-head"><span className="ai-assistant-review-badge" aria-hidden="true"><ThumbIcon path={rating === "up" ? THUMB_UP_PATH : THUMB_DOWN_PATH} /></span><strong id="ai-assistant-review-title">{SHEET_TEXT[rating].title}</strong></div>
      <p id="ai-assistant-review-subtitle" className="ai-assistant-review-subtitle">לא חובה. כל פרט עוזר לנו לשפר את ביבוט.</p>
      <div className="ai-assistant-review-reasons">{REVIEW_REASONS[rating].map((reason, index) => <button key={reason.code} ref={index === 0 ? firstReasonRef : null} type="button" disabled={saving} aria-pressed={reasons.includes(reason.code)} onClick={() => toggleReason(reason.code)}>{reason.label}</button>)}</div>
      <div className="ai-assistant-review-field"><textarea id="ai-assistant-review-comment" name="aiAssistantReviewComment" autoComplete="off" disabled={saving} aria-label="פירוט" placeholder={SHEET_TEXT[rating].placeholder} rows={3} maxLength={MAX_REVIEW_COMMENT_CHARACTERS} value={comment} onChange={(event) => setComment(event.target.value)} /><span className="ai-assistant-review-count" aria-hidden="true">{comment.length}/{MAX_REVIEW_COMMENT_CHARACTERS}</span></div>
      {coolingDown || failed ? <p role="status">{coolingDown ? "שליחת הפירוט מושהית. יש להמתין לסיום ההשהיה ולשלוח שוב." : "הפירוט לא נשלח. נסו שוב."}</p> : null}
      <div className="ai-assistant-review-actions"><button type="submit" className="ai-assistant-review-save" disabled={!canSend || saving || coolingDown}>שליחת פירוט</button><button type="button" className="ai-assistant-review-cancel" onClick={onCancel}>לא עכשיו</button></div>
    </form>
  </div>;
}
